// src/components/PerformanceChart.js
import React from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const PerformanceChart = ({ 
  data, 
  dataKey = "value", 
  currency, 
  chartColor,
  showYAxis = false,
  height = 300,
  tooltipUnit = "Wert",
  showMovingAverages = false
}) => {
  const CURRENCY_SYMBOLS = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥'
  };

  const formatCurrencyValue = (value, currency) => {
    if (value === null || value === undefined) return 'N/A';
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    return `${symbol}${value.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Calculate custom domain to enhance visibility of small changes
  const calculateYAxisDomain = () => {
    if (!data || data.length === 0) return ['auto', 'auto'];
    
    const values = data.flatMap(d => [
      d[dataKey],
      ...(showMovingAverages ? [d.ma50, d.ma200].filter(v => v !== null && v !== undefined) : [])
    ]).filter(v => v !== null && v !== undefined);
    
    if (values.length === 0) return ['auto', 'auto'];
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    // Calculate the range
    const range = max - min;
    
    // Add padding to make small changes more visible
    const padding = range * 0.1;
    
    return [
      min - padding, // Lower bound
      max + padding  // Upper bound
    ];
  };

  // Function to determine date format based on data range
  const getDateFormat = () => {
    if (!data || data.length < 2) return 'dd.MM';
    
    const firstDate = new Date(data[0].date);
    const lastDate = new Date(data[data.length - 1].date);
    const daysDiff = Math.floor((lastDate - firstDate) / (1000 * 60 * 60 * 24));
    
    if (daysDiff <= 1) return 'HH:mm';
    if (daysDiff <= 7) return 'EEE';
    if (daysDiff <= 31) return 'dd. MMM';
    if (daysDiff <= 365) return 'MMM yyyy';
    return 'MMM yyyy';
  };

  // Get ticks for x-axis
  const getTicks = () => {
    if (!data || data.length === 0) return [];

    const dates = data.map(d => d.date);
    const dataLength = dates.length;

    if (dataLength <= 10) return dates;

    // Calculate the appropriate interval based on data length
    const interval = Math.max(1, Math.floor(dataLength / 6));
    
    // Always include first and last date, and evenly spaced dates in between
    return dates.filter((_, index) => 
      index === 0 || index === dataLength - 1 || index % interval === 0
    );
  };

  // Custom tooltip formatter for moving averages
  const customTooltipFormatter = (value, name) => {
    if (name === 'ma50') {
      return [formatCurrencyValue(value, currency), '50-Tage-Durchschnitt'];
    } else if (name === 'ma200') {
      return [formatCurrencyValue(value, currency), '200-Tage-Durchschnitt'];
    } else {
      return [formatCurrencyValue(value, currency), tooltipUnit];
    }
  };

  // Decide whether to use AreaChart or LineChart based on moving averages
  const ChartComponent = showMovingAverages ? LineChart : AreaChart;

  return (
    <div style={{ height: `${height}px`, width: '100%', minWidth: '0', padding: '0 0.25rem' }}>
      <ResponsiveContainer>
        <ChartComponent 
          data={data} 
          margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
        >
          <defs>
            <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={chartColor}
                stopOpacity={0.3}
              />
              <stop
                offset="100%"
                stopColor={chartColor}
                stopOpacity={0.05}
              />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
          <XAxis 
            dataKey="date" 
            tickFormatter={(date) => {
              return format(new Date(date), getDateFormat(), { locale: de });
            }}
            stroke="var(--text-secondary)"
            ticks={getTicks()}
            interval="preserveStartEnd"
            minTickGap={30}
            tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
          />
          {showYAxis && (
            <YAxis 
              domain={calculateYAxisDomain()}
              tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
              tickFormatter={value => value.toLocaleString('de-DE', { maximumFractionDigits: 0 })}
              width={40}
            />
          )}
          {!showYAxis && (
            <YAxis 
              domain={calculateYAxisDomain()}
              hide={true}
            />
          )}
          <Tooltip 
            contentStyle={{ 
                backgroundColor: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '10px'
            }}
            labelStyle={{ color: 'var(--text-primary)', fontWeight: 'bold' }}
            itemStyle={{ color: chartColor }}
            labelFormatter={(value) => format(new Date(value), 'PPP', { locale: de })}
            formatter={customTooltipFormatter}
          />
          
          {/* Main price line/area */}
          {showMovingAverages ? (
            <Line 
              type="monotone" 
              dataKey={dataKey}
              stroke={chartColor}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ) : (
            <Area 
              type="monotone" 
              dataKey={dataKey}
              stroke={chartColor}
              strokeWidth={2}
              fill="url(#colorGradient)"
              isAnimationActive={false}
            />
          )}
          
          {/* Moving average lines - data comes from backend */}
          {showMovingAverages && (
            <>
              <Line 
                type="monotone" 
                dataKey="ma50"
                stroke="#ff7300"
                strokeWidth={1.5}
                strokeDasharray="5 5"
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
              <Line 
                type="monotone" 
                dataKey="ma200"
                stroke="#8884d8"
                strokeWidth={1.5}
                strokeDasharray="10 5"
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
            </>
          )}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
};

export default PerformanceChart;